import { ng } from "@common/ngds";
import { Drawer, PathBuilder } from "@common/components/Rendering";
import type { StyleForPath } from "@common/components/Rendering";
import {
  ArrowId,
  fire_icon_style,
  steel_fire_protection_style,
  timber_fire_protection_style,
  timber_struct_protection_style,
} from "../ViewportStyles";
import type { Extent } from "./drawGeomCrossSection";

// Experimental: helper for visual fire exposure overlays in cross-section view.
interface FireExposureData {
  upperSide: boolean;
  bottomSide: boolean;
  leftSide: boolean;
  rightSide: boolean;
}

// Experimental: gap between the section face and the flame icon, in meters. Kept far enough out
// to clear the steel/timber protection indicator lines drawn closer to the face (offsets 0.01m
// and 0.018m respectively, below), so the flame and protection line don't visually merge. This can
// still overlap the dimension lines (cs_meassure_dist in ViewportStyles.tsx, 0.05m from the
// section face), but App.tsx now auto-hides dimensions whenever the fire indicator turns on, so
// the two no longer need to share the same offset budget.
export const FIRE_ICON_OFFSET = 0.045;

const ALL_SIDES: FireExposureData = { upperSide: true, bottomSide: true, leftSide: true, rightSide: true };

/**
 * Draw a flame icon just outside the midpoint of each enabled face of the cross-section
 * bounding box.
 */
const drawFireIcons = (drawer: Drawer<ArrowId>, geom: Extent, sides: FireExposureData) => {
  const off = FIRE_ICON_OFFSET;
  const midHor = (geom.hor.min + geom.hor.max) / 2;
  const midVer = (geom.ver.min + geom.ver.max) / 2;

  const drawIcon = (p: [number, number]) => {
    drawer.drawSymbol(p, "fire-icon", fire_icon_style, undefined, "fire-icon");
  };

  if (sides.bottomSide) {
    drawIcon([midHor, geom.ver.max + off]);
  }
  if (sides.upperSide) {
    drawIcon([midHor, geom.ver.min - off]);
  }
  if (sides.leftSide) {
    drawIcon([geom.hor.min - off, midVer]);
  }
  if (sides.rightSide) {
    drawIcon([geom.hor.max + off, midVer]);
  }
};

/**
 * Draw fire exposure indicators (flame icons) on the exposed sides of the cross-section
 * bounding box.
 *
 * Coordinate mapping (drawing space after y-negation):
 *   upperSide  → ver.min (top in drawing/SVG)
 *   bottomSide → ver.max (bottom in drawing/SVG)
 *   leftSide   → hor.min
 *   rightSide  → hor.max
 */
export const drawFireExposure = (drawer: Drawer<ArrowId>, geom: Extent, fire: FireExposureData) => {
  drawFireIcons(drawer, geom, fire);
};

/**
 * Draw fire exposure indicators for a circular section: a flame icon on each of the four
 * bounding-box faces. Circular sections have a single face, and rc-design-api reads the
 * "upper side" flag as the entire perimeter, so this is either fully on or fully off - there
 * are no individual sides.
 */
export const drawCircularFireExposure = (drawer: Drawer<ArrowId>, geom: Extent, exposed: boolean) => {
  if (!exposed) {
    return;
  }

  drawFireIcons(drawer, geom, ALL_SIDES);
};

// ================================================================================================
// Experimental: steel fire protection indicators.
//
// Unlike concrete/timber, steel sections don't draw flame icons on the exposed faces. Instead
// we draw a simple line just outside each *protected* (insulated) face, based on the
// ng.design.steel.SectionExposure setting:
//   SECTION_EXPOSURE_ALL_SIDES   -> protected on all four faces
//   SECTION_EXPOSURE_THREE_SIDES -> protected on three faces; the bottom face is left unprotected
//   SECTION_EXPOSURE_FLANGE_ONLY -> protected only at the flanges (top and bottom faces)

// Experimental: gap between the section face and the protection indicator line, in meters.
const STEEL_PROTECTION_OFFSET = 0.01;

interface SteelFireProtectionSides {
  upperSide: boolean;
  bottomSide: boolean;
  leftSide: boolean;
  rightSide: boolean;
}

const NO_SIDES: SteelFireProtectionSides = { upperSide: false, bottomSide: false, leftSide: false, rightSide: false };

/**
 * Map the SectionExposure enum to the set of faces that are fire-protected.
 */
export const steelFireProtectionSidesFromExposure = (
  exposure: ng.design.steel.SectionExposure,
): SteelFireProtectionSides => {
  switch (exposure) {
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES:
      return { upperSide: true, bottomSide: true, leftSide: true, rightSide: true };
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_THREE_SIDES:
      // The bottom face is left unprotected (exposed); the other three faces are protected.
      return { upperSide: true, bottomSide: false, leftSide: true, rightSide: true };
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_FLANGE_ONLY:
      // "Flange" refers to the top and bottom faces of the section.
      return { upperSide: true, bottomSide: true, leftSide: false, rightSide: false };
    default:
      return NO_SIDES;
  }
};

/**
 * Map the SectionExposure enum to the set of faces that are fire-exposed.
 */
export const steelFireExposureSidesFromExposure = (
  exposure: ng.design.steel.SectionExposure,
): SteelFireProtectionSides => {
  switch (exposure) {
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES:
      return ALL_SIDES;
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_THREE_SIDES:
      return { upperSide: true, bottomSide: false, leftSide: true, rightSide: true };
    case ng.design.steel.SectionExposure.SECTION_EXPOSURE_FLANGE_ONLY:
      return { upperSide: true, bottomSide: true, leftSide: false, rightSide: false };
    default:
      return NO_SIDES;
  }
};

const drawProtectionLine = (
  drawer: Drawer<ArrowId>,
  p1: [number, number],
  p2: [number, number],
  style: StyleForPath = steel_fire_protection_style,
) => {
  const path = new PathBuilder();
  path.moveTo(p1);
  path.drawLineTo(p2);
  drawer.drawPath(style, path);
};

/**
 * Draw steel fire protection indicators: a line just outside each protected face of the
 * cross-section bounding box.
 */
export const drawSteelFireProtection = (drawer: Drawer<ArrowId>, geom: Extent, sides: SteelFireProtectionSides) => {
  const off = STEEL_PROTECTION_OFFSET;

  if (sides.bottomSide) {
    drawProtectionLine(drawer, [geom.hor.min, geom.ver.max + off], [geom.hor.max, geom.ver.max + off]);
  }
  if (sides.upperSide) {
    drawProtectionLine(drawer, [geom.hor.min, geom.ver.min - off], [geom.hor.max, geom.ver.min - off]);
  }
  if (sides.leftSide) {
    drawProtectionLine(drawer, [geom.hor.min - off, geom.ver.min], [geom.hor.min - off, geom.ver.max]);
  }
  if (sides.rightSide) {
    drawProtectionLine(drawer, [geom.hor.max + off, geom.ver.min], [geom.hor.max + off, geom.ver.max]);
  }
};

/**
 * Draw a steel fire protection ring for a circular section. Circular sections have a single face,
 * so unlike the rectangular case there's no per-side distinction between exposure options -
 * whenever fire protection applies, the whole perimeter is protected.
 */
export const drawCircularSteelFireProtection = (drawer: Drawer<ArrowId>, geom: Extent) => {
  const cx = (geom.hor.min + geom.hor.max) / 2;
  const cy = (geom.ver.min + geom.ver.max) / 2;
  const radius = Math.max(geom.hor.max - geom.hor.min, geom.ver.max - geom.ver.min) / 2;
  if (radius <= 0) {
    return;
  }

  drawer.drawCircle([cx, cy], radius + STEEL_PROTECTION_OFFSET, steel_fire_protection_style);
};

/**
 * Draw a flame icon just outside the midpoint of each fire-exposed face of the cross-section
 * bounding box.
 */
export const drawSteelFireIcons = (drawer: Drawer<ArrowId>, geom: Extent, sides: SteelFireProtectionSides) => {
  drawFireIcons(drawer, geom, sides);
};

// ================================================================================================
// Experimental: timber fire protection indicators.
//
// Timber uses two independent layers of indicator, drawn outside the section:
//   1. A thin border around the section, shown whenever a protecting material is selected
//      (SXInputTimberFireSettings "Protecting material" dropdown, any value other than "none").
//      Faces where the structural protection switch (below) is enabled are excluded from this
//      border, since the structural protection already indicates the face is protected.
//   2. A thicker line drawn outside a specific face when its "protecting structure" switch
//      (Top/Bottom/Left/Right) is enabled - drawn further out than the border.

// Experimental: gap between the section face and the protecting-material border, in meters.
const TIMBER_PROTECTION_BORDER_OFFSET = 0.008;
// Experimental: gap between the section face and the thicker structural-protection side line.
// Kept further out than the border so both indicators stay visible on protected faces.
const TIMBER_STRUCT_PROTECTION_OFFSET = 0.018;

interface TimberStructProtectionSides {
  upperSide: boolean;
  bottomSide: boolean;
  leftSide: boolean;
  rightSide: boolean;
}

/**
 * Draw the thin border indicating a protecting material is applied around the section. Faces
 * listed in `excludeSides` are skipped, since those faces already get the thicker structural
 * protection line and don't need the additional border (avoids double-drawing on that side).
 */
export const drawTimberFireProtectionBorder = (
  drawer: Drawer<ArrowId>,
  geom: Extent,
  excludeSides?: TimberStructProtectionSides,
) => {
  const off = TIMBER_PROTECTION_BORDER_OFFSET;

  if (!excludeSides?.bottomSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min - off, geom.ver.max + off],
      [geom.hor.max + off, geom.ver.max + off],
      timber_fire_protection_style,
    );
  }
  if (!excludeSides?.upperSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min - off, geom.ver.min - off],
      [geom.hor.max + off, geom.ver.min - off],
      timber_fire_protection_style,
    );
  }
  if (!excludeSides?.leftSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min - off, geom.ver.min - off],
      [geom.hor.min - off, geom.ver.max + off],
      timber_fire_protection_style,
    );
  }
  if (!excludeSides?.rightSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.max + off, geom.ver.min - off],
      [geom.hor.max + off, geom.ver.max + off],
      timber_fire_protection_style,
    );
  }
};

/**
 * Draw the thin border indicating a protecting material is applied around a circular section.
 */
export const drawCircularTimberFireProtectionBorder = (drawer: Drawer<ArrowId>, geom: Extent) => {
  const cx = (geom.hor.min + geom.hor.max) / 2;
  const cy = (geom.ver.min + geom.ver.max) / 2;
  const radius = Math.max(geom.hor.max - geom.hor.min, geom.ver.max - geom.ver.min) / 2;
  if (radius <= 0) {
    return;
  }

  drawer.drawCircle([cx, cy], radius + TIMBER_PROTECTION_BORDER_OFFSET, timber_fire_protection_style);
};

/**
 * Draw the thicker structural-protection indicator line outside each enabled face of the
 * cross-section bounding box.
 */
export const drawTimberStructProtectionSides = (
  drawer: Drawer<ArrowId>,
  geom: Extent,
  sides: TimberStructProtectionSides,
) => {
  const off = TIMBER_STRUCT_PROTECTION_OFFSET;

  if (sides.bottomSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min, geom.ver.max + off],
      [geom.hor.max, geom.ver.max + off],
      timber_struct_protection_style,
    );
  }
  if (sides.upperSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min, geom.ver.min - off],
      [geom.hor.max, geom.ver.min - off],
      timber_struct_protection_style,
    );
  }
  if (sides.leftSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.min - off, geom.ver.min],
      [geom.hor.min - off, geom.ver.max],
      timber_struct_protection_style,
    );
  }
  if (sides.rightSide) {
    drawProtectionLine(
      drawer,
      [geom.hor.max + off, geom.ver.min],
      [geom.hor.max + off, geom.ver.max],
      timber_struct_protection_style,
    );
  }
};

/**
 * Draw a flame icon just outside the midpoint of each face of the cross-section bounding box,
 * indicating that fire acts on every side of the column (independent of the protecting
 * material/structure settings, which only affect how the char depth is calculated).
 */
export const drawTimberFireIcons = (drawer: Drawer<ArrowId>, geom: Extent) => {
  drawFireIcons(drawer, geom, ALL_SIDES);
};
